import { useState } from 'react';
import { Sliders, X, Plus, Trash2, Copy, Check } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';

const EMPTY_FIELD = { name: '', type: 'string', description: '', optional: false, options: [] };
const inputClass = 'w-full border rounded-md px-2 py-1 text-xs bg-[var(--app-surface)] border-[var(--app-border)]';

export function parseOptions(text) {
  return String(text || '').split(',').map((o) => o.trim()).filter(Boolean);
}

function copyToClipboard(text, onSuccess) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(() => {});
  }
}

function DrawerHeader({ fields, onClose }) {
  const reqCount = fields.filter((f) => !f.optional && f.name).length;
  const optCount = fields.filter((f) => f.optional && f.name).length;

  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--app-border)]">
      <div className="flex items-center gap-2">
        <Sliders size={15} className="text-primary-600" />
        <span className="text-sm font-semibold">Input Contract</span>
        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--app-surface-hover,rgba(0,0,0,0.05))] text-secondary-500">
          {fields.length}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-secondary-500">
          {reqCount} req{optCount > 0 ? `, ${optCount} opt` : ''}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded text-secondary-500 hover:text-primary-600"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function resetCopiedState(setCopied, name) {
  setTimeout(() => {
    setCopied((prev) => (prev === name ? null : prev));
  }, 1500);
}

function VariableChips({ fields }) {
  const [copied, setCopied] = useState(null);
  const namedFields = fields.filter((f) => f.name?.trim());

  if (!namedFields.length) return null;

  const handleCopy = (varTag, name) => {
    copyToClipboard(varTag, () => {
      setCopied(name);
      resetCopiedState(setCopied, name);
    });
  };

  return (
    <div className="px-3 py-2 border-b border-[var(--app-border)] bg-secondary-500/5 space-y-1">
      <div className="text-[10px] font-medium text-secondary-500">Click to copy variable:</div>
      <div className="flex flex-wrap gap-1">
        {namedFields.map((f) => {
          const varTag = `{{input.${f.name}}}`;
          const isCopied = copied === f.name;
          return (
            <button
              key={f.name}
              type="button"
              onClick={() => handleCopy(varTag, f.name)}
              title={`Copy ${varTag} to clipboard`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono border transition-colors ${
                isCopied
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600'
                  : 'border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-600 hover:border-primary-500 hover:text-primary-600'
              }`}
            >
              {isCopied ? <Check size={10} className="text-emerald-600" /> : <Copy size={10} />}
              <span>{varTag}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FieldRow({ field, index, onUpdate, onRemove }) {
  const isString = field.type === 'string';

  return (
    <div className="p-2.5 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] space-y-2">
      <div className="flex items-center gap-2">
        <input
          className={`${inputClass} flex-1 font-mono text-xs`}
          placeholder="name (e.g. user_id)"
          value={field.name}
          onChange={(e) => onUpdate({ ...field, name: e.target.value })}
        />
        <select
          className={`${inputClass} w-24`}
          value={field.type}
          onChange={(e) => onUpdate({ ...field, type: e.target.value })}
        >
          <option value="string">string</option>
          <option value="number">number</option>
          <option value="boolean">boolean</option>
        </select>
        <label className="flex items-center gap-1 text-[11px] text-secondary-500 whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={!!field.optional}
            onChange={(e) => onUpdate({ ...field, optional: e.target.checked })}
          />
          optional
        </label>
        <button
          type="button"
          onClick={() => onRemove(index)}
          title="Delete field"
          className="p-1 text-secondary-400 hover:text-red-500 rounded"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <input
        className={inputClass}
        placeholder="description (helps LLM supply the right parameter)"
        value={field.description || ''}
        onChange={(e) => onUpdate({ ...field, description: e.target.value })}
      />

      {isString && (
        <input
          className={inputClass}
          placeholder="options enum (comma-separated, e.g. urgent, normal, low)"
          value={(field.options || []).join(', ')}
          onChange={(e) => onUpdate({ ...field, options: parseOptions(e.target.value) })}
        />
      )}
    </div>
  );
}

function EmptyFieldsNotice({ onAdd }) {
  return (
    <div className="text-center py-6 px-4 space-y-3">
      <p className="text-xs text-secondary-500">
        No input parameters defined yet. Callers will invoke this workflow with no required arguments.
      </p>
      <Button size="sm" variant="secondary" onClick={onAdd}>
        <Plus size={13} className="inline mr-1" /> Add first parameter
      </Button>
    </div>
  );
}

export default function InputContractDrawer({ fields = [], setFields, onClose }) {
  const update = (idx, next) => setFields(fields.map((f, i) => (i === idx ? next : f)));
  const remove = (idx) => setFields(fields.filter((_, i) => i !== idx));
  const addField = () => setFields([...fields, { ...EMPTY_FIELD }]);

  return (
    <div className="h-full w-96 shrink-0 flex flex-col rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] overflow-hidden">
      <DrawerHeader fields={fields} onClose={onClose} />
      <VariableChips fields={fields} />

      <div className="flex-1 overflow-y-auto p-2.5 space-y-2">
        <p className="text-[11px] text-secondary-500">
          What agents or API callers must pass. String fields with options become a validated enum.
        </p>

        {fields.length === 0 ? (
          <EmptyFieldsNotice onAdd={addField} />
        ) : (
          fields.map((field, idx) => (
            <FieldRow
              key={idx}
              field={field}
              index={idx}
              onUpdate={(next) => update(idx, next)}
              onRemove={remove}
            />
          ))
        )}
      </div>

      {fields.length > 0 && (
        <div className="p-2.5 border-t border-[var(--app-border)] bg-[var(--app-surface)]">
          <Button size="sm" variant="secondary" onClick={addField} className="w-full">
            <Plus size={13} className="inline mr-1" /> Add Parameter
          </Button>
        </div>
      )}
    </div>
  );
}
